import { SimpleDocCommand } from '../../DocsApp/commands/v5/types';
import { StandardToolResult } from '../../../types/standardToolResults';
import { CommandExecutionContext } from '../../../types/commands';
import { getApplicationForEntity } from '../../../services/applicationRegistry';
import fetchService from '../../../services/fetchService';
import {
  generateGrokImage,
  searchAndDownloadUnsplash,
  getApiBaseUrl,
  ImageResult,
  ImageMetadata
} from '../../../services/imageService';
import { schemaToParameters } from '../../../utils/schemaToParameters';

export const image_add: SimpleDocCommand = {
  id: 'image_add',
  logicalName: 'image_add',
  version: 'v2',

  description: `Insert images into Notes, Canvas, and Presentations using AI generation, stock photos, or direct URLs.

**SUPPORTED ENTITIES:**
- Notes (note_*)
- Canvas (canvas_*)
- Presentations (presentation_*)
- Use doc_write for Documents (doc_*)

**NOT SUPPORTED:**
- Sheets (sheet_*) - Images cannot be inserted into Sheets
- Documents (doc_*) - Use doc_write instead

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
IMAGE SOURCES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• grok - xAI/Grok image generation (PRIMARY AI GENERATION)
  Model: grok-imagine-image | Fast, high-quality, customizable

• unsplash - Professional stock photos
  High-quality photos | Filters: orientation, color | Auto-attribution

• url - Direct image URL (https://)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
DECISION GUIDE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Need AI-generated image? → source: "grok" (flexible, fast)
Stock photo? → source: "unsplash" (auto-attribution)
Existing URL? → source: "url"

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
LOCATION REQUIREMENTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Notes: Optional (defaults to end)
  { at: "end" | "start" | "cursor", width: 600 }  // width in pixels (default: 600)

Canvas: REQUIRED { x, y }. Optional: width, height, z
  Center origin (0,0) | Negative coordinates allowed

Presentations: REQUIRED { slideNumber, x, y }. Optional: width, height, z
  Top-left origin (0,0) | Slide dimensions: 800x450px

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
EXAMPLES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Grok to Notes:
{ "entityId": "note_abc", "source": "grok", "prompt": "modern infographic showing revenue growth", "generationOptions": { "width": 1792, "height": 1024 } }

Grok to Canvas:
{ "entityId": "canvas_xyz", "source": "grok", "prompt": "abstract geometric art", "generationOptions": { "width": 1024, "height": 1024 }, "location": { "x": 0, "y": 0, "width": 600, "height": 600 } }

Grok to Presentation:
{ "entityId": "presentation_pitch", "source": "grok", "prompt": "business growth chart", "generationOptions": { "width": 1024, "height": 768 }, "location": { "slideNumber": 2, "x": 100, "y": 100, "width": 700, "height": 400 } }

Unsplash to Notes:
{ "entityId": "note_ideas", "source": "unsplash", "query": "creative workspace" }

URL to Notes:
{ "entityId": "note_research", "source": "url", "url": "https://example.com/chart.png" }`,

  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'Entity ID (note_*, canvas_*, presentation_*).',
        required: true,
        validator: (value: string) => {
          if (!value) return 'entityId is required';
          if (value.startsWith('doc_') || value.startsWith('pdf_') || value.startsWith('file_')) {
            return 'image_add is for Notes, Canvas, and Presentations only. Use doc_write for Documents (doc_*) or PDFs (pdf_*/file_*).';
          }
          if (value.startsWith('sheet_')) {
            return 'Images cannot be inserted into Sheets. Use image_add with Notes, Canvas, or Presentations instead.';
          }
          const validPrefixes = ['note_', 'canvas_', 'presentation_'];
          if (!validPrefixes.some(prefix => value.startsWith(prefix))) {
            return `entityId must start with: ${validPrefixes.join(', ')}. Use doc_write for Documents.`;
          }
          return null;
        }
      },
      {
        name: 'source',
        type: 'string',
        description: 'Image source: grok | unsplash | url.',
        required: true,
        enum: ['grok', 'unsplash', 'url'],
      },
      {
        name: 'location',
        type: 'object',
        description: 'Where to insert the image. Required for Canvas/Presentations, optional for Notes.',
        required: false,
        validator: (value: any, params: any) => {
          const entityType = params.entityId?.split('_')[0];
          if ((entityType === 'canvas' || entityType === 'presentation') && !value) {
            return `location is required for ${entityType}`;
          }
          if (entityType === 'canvas' && value) {
            if (value.x === undefined || value.y === undefined) {
              return 'location must include x and y for Canvas';
            }
          }
          if (entityType === 'presentation' && value) {
            if (!value.slideNumber) {
              return 'location must include slideNumber for Presentations (1-based, e.g., slideNumber: 1)';
            }
            if (value.slideNumber < 1) {
              return 'slideNumber must be >= 1';
            }
          }
          return null;
        }
      },
      {
        name: 'url',
        type: 'string',
        description: 'Image URL (required when source is "url").',
        required: false,
        validator: (value: string, params: any) => {
          if (params.source === 'url' && !value) return 'url is required when source is "url"';
          if (value && !value.startsWith('http')) return 'url must start with http:// or https://';
          return null;
        }
      },
      {
        name: 'query',
        type: 'string',
        description: 'Search query for Unsplash (required when source is "unsplash").',
        required: false,
        validator: (value: string, params: any) => {
          if (params.source === 'unsplash' && !value) return 'query is required when source is "unsplash"';
          if (value && value.length < 2) return 'query must be at least 2 characters';
          return null;
        }
      },
      {
        name: 'prompt',
        type: 'string',
        description: 'Image generation prompt (required when source is "grok").',
        required: false,
        validator: (value: string, params: any) => {
          if (params.source === 'grok' && (!value || value.trim().length === 0)) return 'prompt is required when source is "grok"';
          if (value && value.length > 1000) return 'prompt must be less than 1000 characters';
          return null;
        }
      },
      { name: 'generationOptions', type: 'object', description: 'Options for Grok image generation. Options: width (256-2048), height (256-2048), seed, variants.', required: false },
      { name: 'unsplashOptions', type: 'object', description: 'Options for Unsplash search. Options: orientation (landscape|portrait|squarish), color.', required: false },
      { name: 'alt', type: 'string', description: 'Alt text for accessibility.', required: false },
      { name: 'attribution', type: 'boolean', description: 'Include attribution text for Unsplash images. Default: true.', required: false, defaultValue: true }
    ]
  },

  formatViewData: (result: any, toolInput: any) => {
    try {
      if (result?.status === 'success' && result?.data) {
        const data = result.data;
        return {
          type: 'image_insertion',
          summary: result.message || 'Image added successfully',
          data: {
            imageUrl: data.imageUrl,
            source: data.source,
            entityType: data.entityType,
            entityId: data.entityId,
            metadata: { width: data.metadata?.width, height: data.metadata?.height, photographer: data.metadata?.photographer, model: data.metadata?.model }
          },
          meta: { source: data.source, dimensions: data.metadata?.width && data.metadata?.height ? `${data.metadata.width}×${data.metadata.height}` : undefined }
        };
      }
      return { type: 'tool_summary', summary: result?.message || 'Image operation completed', data: result?.data || {} };
    } catch (error) {
      console.warn('[image_add] Error in formatViewData:', error);
      return { type: 'tool_summary', summary: 'Image operation completed', data: {} };
    }
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    console.log('[image_add] Starting image insertion:', { entityId: params.entityId, source: params.source });

    try {
      const entityType = params.entityId.split('_')[0];
      if (!['note', 'canvas', 'presentation'].includes(entityType)) {
        throw new Error('image_add only supports Notes, Canvas, and Presentations');
      }

      let imageResult: ImageResult;
      switch (params.source) {
        case 'grok':
          imageResult = await generateGrokImage(params.prompt, params.generationOptions);
          break;
        case 'unsplash':
          imageResult = await searchAndDownloadUnsplash(params.query, params.unsplashOptions);
          break;
        case 'url':
          imageResult = await validateImageUrl(params);
          break;
        default:
          throw new Error(`Invalid source: ${params.source}. Use: grok, unsplash, or url`);
      }

      console.log('[image_add] Image obtained:', { url: imageResult.url ? (imageResult.url.length > 60 ? imageResult.url.substring(0, 60) + '...' : imageResult.url) : 'undefined', source: imageResult.metadata.source });

      const app = await getApplicationForEntity(params.entityId);
      if (!app) throw new Error(`No application found for entity: ${params.entityId}`);

      await insertImageIntoEntity(app, entityType, imageResult.url, imageResult.metadata, params);

      console.log('[image_add] ✅ Image inserted successfully');

      const successMessage = buildSuccessMessage(params.source, imageResult.metadata, entityType);
      return {
        status: 'success',
        message: successMessage,
        data: { imageUrl: imageResult.url, source: params.source, metadata: imageResult.metadata, entityId: params.entityId, entityType },
        format: 'plaintext'
      };

    } catch (error: any) {
      console.error('[image_add] Error:', error);
      return { status: 'error', message: `Failed to add image: ${error.message}`, error: { code: 'image_add_failed', category: 'execution' as const, details: error.message }, format: 'plaintext' };
    }
  }
};

async function validateImageUrl(params: any): Promise<ImageResult> {
  const { url } = params;
  console.log('[image_add] Using direct URL:', url);
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    throw new Error('URL must start with http:// or https://');
  }
  return { url, metadata: { source: 'url' } };
}

async function insertImageIntoEntity(app: any, entityType: string, imageUrl: string, metadata: ImageMetadata, params: any): Promise<void> {
  const alt = params.alt || metadata.description || metadata.fileName || 'Image';

  if (entityType === 'note') {
    const width = params.location?.width || 600;
    const content = { className: 'note-image', attrs: { position: { type: 'flow' }, semantic: { src: imageUrl, alt }, width, height: null } };
    const target = params.location?.at ? { at: params.location.at } : params.location?.before ? { before: params.location.before } : params.location?.after ? { after: params.location.after } : { at: 'end' };
    await app.handleEntityWrite({ operation: 'insert', target, content });
    if (params.attribution !== false && metadata.source === 'unsplash' && metadata.photographer) {
      try {
        await app.handleEntityWrite({ operation: 'insert', target: { at: 'end' }, content: { className: 'note-paragraph', attrs: { text: `*Photo by ${metadata.photographer} on Unsplash*` } } });
      } catch (attrError: any) {
        console.warn('[image_add] Failed to add attribution (image inserted successfully):', attrError.message);
      }
    }
  } else if (entityType === 'canvas') {
    const x = params.location?.x ?? 0;
    const y = params.location?.y ?? 0;
    const width = params.location?.width ?? metadata.width ?? 400;
    const height = params.location?.height ?? metadata.height ?? 300;
    const content = { className: 'canvas-image', attrs: { position: { type: 'absolute', x, y, width, height, z: params.location?.z || 1 }, semantic: { src: imageUrl, alt } } };
    await app.handleEntityWrite({ operation: 'insert', content });
  } else if (entityType === 'presentation') {
    const slideNumber = params.location?.slideNumber ?? 1;
    const x = params.location?.x ?? 100;
    const y = params.location?.y ?? 100;
    const width = params.location?.width ?? metadata.width ?? 400;
    const height = params.location?.height ?? metadata.height ?? 300;
    const content = { className: 'slide-image', attrs: { position: { type: 'absolute', x, y, width, height, z: params.location?.z || 1 }, semantic: { src: imageUrl, alt } } };
    await app.handleEntityWrite({ operation: 'insert', target: { slideNumber }, content });
  }
}

function buildSuccessMessage(source: string, metadata: ImageMetadata, entityType: string): string {
  const entityName = entityType === 'note' ? 'note' : entityType === 'canvas' ? 'canvas' : 'presentation';
  switch (source) {
    case 'grok': return `Generated image with Grok and added to ${entityName}.\n\nPrompt: "${metadata.prompt?.substring(0, 100)}${metadata.prompt && metadata.prompt.length > 100 ? '...' : ''}"`;
    case 'unsplash': return `Added Unsplash photo to ${entityName}.\n\nPhoto by ${metadata.photographer} on Unsplash\nDescription: ${metadata.description || 'N/A'}`;
    case 'url': return `Added image from URL to ${entityName}.`;
    default: return `Added image to ${entityName}.`;
  }
}
